/**
 * BenchmarkComparisonChart — Chunk 83: Portfolio Benchmarking & Index Comparison
 * Extended in Chunk 158: custom date range (from/to props) + alpha badge
 *
 * Dual-line Recharts chart showing portfolio return vs a selected benchmark,
 * both indexed to 100 at the start of the chosen time range.
 * Displays alpha, beta, and correlation below the chart.
 */

import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAppSelector } from '../../store/hooks';
import {
  getBenchmarkComparison,
  type BenchmarkId,
  type BenchmarkRange,
  type BenchmarkCompareResult,
} from '../../api/benchmarks';

const BENCHMARK_OPTIONS: { value: BenchmarkId; label: string; color: string }[] = [
  { value: 'BTC', label: 'Bitcoin (BTC)', color: '#f59e0b' },
  { value: 'ETH', label: 'Ethereum (ETH)', color: '#8b5cf6' },
  { value: 'SPX', label: 'S&P 500', color: '#94a3b8' },
  { value: 'TOTAL3', label: 'Crypto Market (TOTAL3)', color: '#06b6d4' },
];

const RANGE_OPTIONS: { value: BenchmarkRange; label: string }[] = [
  { value: '3m', label: '3M' },
  { value: '6m', label: '6M' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'All' },
];

function formatAlpha(v: number | null): string {
  if (v === null) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)} pp`;
}

function formatBeta(v: number | null): string {
  if (v === null) return '—';
  return v.toFixed(2);
}

function formatCorrelation(v: number | null): string {
  if (v === null) return '—';
  return v.toFixed(2);
}

interface Props {
  /** Pre-populated userId — if omitted, falls back to the authenticated user */
  userId?: string;
  /**
   * Custom range start date (ISO string, e.g. '2023-01-01').
   * When provided alongside `to`, overrides the internal range selector (Chunk 158).
   */
  from?: string;
  /**
   * Custom range end date (ISO string, e.g. '2023-12-31').
   * When provided alongside `from`, overrides the internal range selector (Chunk 158).
   */
  to?: string;
}

const BenchmarkComparisonChart: React.FC<Props> = ({ userId: propUserId, from: propFrom, to: propTo }) => {
  const authUserId = useAppSelector((state) => state.auth.user?.id);
  const userId = propUserId ?? authUserId ?? '';

  const isCustomRange = Boolean(propFrom && propTo);

  const [benchmark, setBenchmark] = useState<BenchmarkId>('BTC');
  const [range, setRange] = useState<BenchmarkRange>('1y');
  const [data, setData] = useState<BenchmarkCompareResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    let mounted = true;
    setIsLoading(true);
    setError(null);

    getBenchmarkComparison(userId, benchmark, range, propFrom, propTo)
      .then((result) => {
        if (mounted) setData(result);
      })
      .catch((err: unknown) => {
        if (mounted) {
          const msg =
            err instanceof Error ? err.message : 'Failed to load benchmark comparison';
          setError(msg);
        }
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [userId, benchmark, range, propFrom, propTo]);

  const benchmarkMeta =
    BENCHMARK_OPTIONS.find((b) => b.value === benchmark) ?? BENCHMARK_OPTIONS[0];

  const hasSeries = (data?.series ?? []).length >= 2;
  // True when we have time points but all benchmark values are null (e.g. SPX key not configured)
  const allBenchmarkNull =
    hasSeries && (data?.series ?? []).every((pt) => pt.benchmark === null);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6 space-y-4">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-white">Benchmark Comparison</h2>
      </div>

      {/* Benchmark selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        {BENCHMARK_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setBenchmark(opt.value)}
            className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
              benchmark === opt.value
                ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-500'
            }`}
          >
            {opt.value === 'SPX' ? 'S&P 500' : opt.value}
          </button>
        ))}
      </div>

      {/* Range selector — hidden when external custom range is provided (Chunk 158) */}
      {!isCustomRange && (
        <div className="flex flex-wrap gap-1 mb-4">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setRange(opt.value)}
              className={`px-2.5 py-1 text-xs font-medium rounded border transition-colors ${
                range === opt.value
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Alpha badge — Chunk 158 */}
      {!isLoading && hasSeries && !allBenchmarkNull && data?.alpha != null && (
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${
              data.alpha >= 0
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-red-500/20 text-red-400'
            }`}
          >
            {data.alpha >= 0
              ? `▲ Outperforming ${benchmarkMeta.value === 'SPX' ? 'S&P 500' : benchmarkMeta.value} by ${Math.abs(data.alpha).toFixed(2)} pp`
              : `▼ Underperforming ${benchmarkMeta.value === 'SPX' ? 'S&P 500' : benchmarkMeta.value} by ${Math.abs(data.alpha).toFixed(2)} pp`}
          </span>
        </div>
      )}

      {/* Chart area */}
      {isLoading ? (
        <div className="h-56 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : error ? (
        <div className="h-56 flex items-center justify-center">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      ) : !hasSeries ? (
        <div className="h-56 flex items-center justify-center">
          <p className="text-gray-500 text-sm">
            {data?.message ?? 'No data available for this range'}
          </p>
        </div>
      ) : allBenchmarkNull ? (
        <div className="h-56 flex flex-col items-center justify-center gap-2 text-center px-4">
          <svg
            className="w-8 h-8 text-gray-600 mb-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
            />
          </svg>
          <p className="text-gray-400 text-sm font-medium">
            {benchmark === 'SPX'
              ? 'S\u0026P 500 data not yet available \u2014 check back after market close'
              : `${benchmarkMeta.label} data not yet available`}
          </p>
          {benchmark === 'SPX' && (
            <p className="text-gray-600 text-xs">
              Configure <code className="font-mono text-gray-500">ALPHAVANTAGE_API_KEY</code> to enable SPX ingestion
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <div className="min-w-[320px]">
          <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data!.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis
                dataKey="date"
                hide
                tick={{ fill: '#9ca3af', fontSize: 10 }}
              />
              <YAxis
                tickFormatter={(v: number) => `${v.toFixed(0)}`}
                tick={{ fill: '#9ca3af', fontSize: 10 }}
                width={45}
                domain={['auto', 'auto']}
              />
              <Tooltip
                formatter={(value: unknown) =>
                  value != null ? `${(value as number).toFixed(2)}` : '—'
                }
                labelFormatter={(label: unknown) => {
                  const dateStr = typeof label === 'string' ? label : String(label);
                  return new Date(dateStr).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  });
                }}
                contentStyle={{
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#e5e7eb',
                  fontSize: '12px',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }}
              />
              <Line
                type="monotone"
                dataKey="portfolio"
                name="Portfolio"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="benchmark"
                name={benchmark === 'SPX' ? 'S&P 500' : benchmarkMeta.label}
                stroke={benchmark === 'SPX' ? '#94a3b8' : benchmarkMeta.color}
                strokeWidth={benchmark === 'SPX' ? 1.5 : 2}
                strokeDasharray={benchmark === 'SPX' ? '6 3' : '5 4'}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
          </div>
        </div>
      )}

      {/* Stats row */}
      {!isLoading && hasSeries && !allBenchmarkNull && (
        <div className="grid grid-cols-3 gap-4 pt-2 border-t border-gray-800">
          <div>
            <p className="text-xs text-gray-400 mb-1">Alpha</p>
            <p
              className={`text-lg font-bold font-mono tabular-nums ${
                (data?.alpha ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
              }`}
              title="Portfolio total return minus benchmark total return (percentage points)"
            >
              {formatAlpha(data?.alpha ?? null)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Beta</p>
            <p
              className="text-lg font-bold font-mono tabular-nums text-amber-400"
              title="Sensitivity of portfolio returns relative to the benchmark"
            >
              {formatBeta(data?.beta ?? null)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Correlation</p>
            <p
              className="text-lg font-bold font-mono tabular-nums text-violet-400"
              title="Pearson correlation of daily returns between portfolio and benchmark"
            >
              {formatCorrelation(data?.correlation ?? null)}
            </p>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-gray-600 italic">
        Past performance does not indicate future results.
      </p>
    </div>
  );
};

export default BenchmarkComparisonChart;
